import type { ProductAnalysisRow } from "./productAnalysisModel";
import { sellthroughCategories, sellthroughSeasons, sellthroughTotal, sortSellthrough, type SellthroughProduct, type SellthroughSort, type SellthroughTotal } from "./productSellthroughModel";

export type SellthroughGrouping = "season-category" | "category-season";
export type SellthroughGroup = {
  label: string; kind: "season" | "category"; products: SellthroughProduct[];
  total: SellthroughTotal; children?: SellthroughGroup[];
};

// Presentation order only; quantities and weighted ratios remain owned by the
// existing Sell-through model and the unchanged canonical Product API.
export function sellthroughGroups(products: SellthroughProduct[], grouping: SellthroughGrouping, sort: SellthroughSort): SellthroughGroup[] {
  const seasonGroup = (season: string, rows: SellthroughProduct[]): SellthroughGroup => ({
    label: season, kind: "season", products: rows, total: sellthroughTotal(season, rows),
  });
  if (grouping === "category-season") {
    const categories = [...new Set(products.map(row => row.category))].map(label => {
      const rows = products.filter(row => row.category === label);
      return { ...sellthroughTotal(label, rows), rows };
    });
    return sortSellthrough(categories, sort).map(category => ({
      label: category.label, kind: "category", products: category.rows, total: category,
      children: sellthroughSeasons.map(season => seasonGroup(season, category.rows.filter(row => row.season === season))).filter(group => group.products.length > 0),
    }));
  }
  return sellthroughSeasons.map(season => {
    const rows = products.filter(row => row.season === season);
    return { ...seasonGroup(season, rows), children: sortSellthrough(sellthroughCategories(products, season), sort).map(category => ({
      label: category.label, kind: "category" as const, products: rows.filter(row => row.category === category.label), total: category,
    })) };
  });
}

export function sellthroughProductParams(scope: string, brand: string, products: SellthroughProduct[]) {
  if (!brand || !products.length || products.length > 25) throw new Error("Invalid Sell-through detail scope");
  const params = new URLSearchParams(scope);
  params.set("brands", brand);
  params.set("skus", products.map(row => row.label).sort().join(","));
  params.set("table", "products"); params.set("offset", "0"); params.set("limit", String(products.length));
  return params;
}

export function verifiedSellthroughProducts(response: { release_id: string; table: string; rows: ProductAnalysisRow[]; total_rows: number }, release: string, expected: SellthroughProduct[]) {
  if (response.release_id !== release || response.table !== "products" || response.rows.length !== expected.length || response.total_rows !== expected.length) throw new Error("Sell-through product response does not match the active scope");
  const result = new Map<string, ProductAnalysisRow>();
  for (const row of response.rows) {
    const source = expected.find(product => product.label === row.label);
    if (!source || result.has(row.label) || source.category !== row.category || source.season !== row.season) throw new Error("Sell-through product identity changed");
    for (const key of ["import_units", "sales_units", "stock_units", "sellthrough_percent"] as const) {
      const a = source[key], b = row[key];
      if (a === null ? b !== null : b === null || !Number.isFinite(b) || Math.abs(a - b) > 1e-9) throw new Error("Sell-through product quantities changed");
    }
    result.set(row.label, row);
  }
  return result;
}
